import * as React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { LoadingButton } from '@mui/lab';
import { Alert, AlertTitle, Box, Button, MenuItem, TextField, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { postCustomerResidencyDoc, putUserAddress, putUserEmail, putUserName, putUserPhone } from 'src/api/oApi';
import { countries } from 'src/helpers/countries';
import { UserItem } from 'src/models/users';
import { generalStore } from 'src/stores/GeneralStore';

type ChangeDataMode = 'email' | 'phone' | 'name' | 'address';

/** Message the backend returns when Mangopay rejects the change for a missing SCA proxy consent. */
export const SCA_CONSENT_REQUIRED_ERROR = 'SCA_CONSENT_REQUIRED';

/** Detects the specific SCA-consent error envelope (`err.response.data.error.message === 'SCA_CONSENT_REQUIRED'`). */
const isScaConsentRequiredError = (error: any): boolean =>
  error?.response?.data?.error?.message === SCA_CONSENT_REQUIRED_ERROR;

/**
 * Routes API errors: an SCA-consent rejection triggers the dedicated overlay
 * (via `onScaConsentRequired`) instead of the generic error toast.
 */
const handleApiError = (error: any, onScaConsentRequired?: () => void) => {
  if (isScaConsentRequiredError(error)) {
    if (onScaConsentRequired) onScaConsentRequired();
    return;
  }
  generalStore.setError(error?.response?.data?.error?.message || error?.response?.data?.error || error?.message || 'Error on Operation');
};

/**
 * Overlay explaining that the data change could not be applied at Mangopay
 * because the user's SCA proxy consent is missing (the user must complete it in-app).
 */
const ScaConsentRequiredDialog: React.FC<{ open: boolean; onClose: () => void; onSkip?: () => void }> = ({ open, onClose, onSkip }) => {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>SCA consent required</DialogTitle>
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 2 }}>
          <AlertTitle>Change not applied at Mangopay</AlertTitle>
          The change could not be applied at Mangopay because the user's SCA consent is not in place.
        </Alert>
        <DialogContentText>
          Mangopay requires the user to grant the SCA proxy consent for this type of change. Either ask the user to grant consent
          in-app and retry, or skip the Mangopay update for now and apply the change on the platform only — the Mangopay data does
          not need to be 100% in sync.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        {onSkip ? (
          <Button onClick={onSkip} variant="outlined" color="warning">
            Skip Mangopay update &amp; apply anyway
          </Button>
        ) : null}
        <Button onClick={onClose} variant="contained" color="primary">
          Understood
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/**
 * Wizard to change user data (Email / Phone / Name / Address).
 * Name and address changes require uploading a supporting document
 * (name certificate / Meldezettel) first; the inputs stay disabled until
 * the upload succeeded.
 */
// eslint-disable-next-line @typescript-eslint/naming-convention
export default function ChangeDataDialog({
  open,
  setOpen,
  userData,
  forceReload,
}: {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  userData: UserItem;
  forceReload?: () => void;
}) {
  const [mode, setMode] = React.useState<ChangeDataMode>('email');
  const [scaBlockedOpen, setScaBlockedOpen] = React.useState(false);
  // The failed submit, ready to re-run with skipMangopayUpdate=true if the operator chooses to skip.
  const [scaRetry, setScaRetry] = React.useState<{ run: () => void } | null>(null);

  const handleClose = () => {
    setOpen(false);
  };

  const onSuccess = (message: string) => {
    generalStore.setMessage(message);
    setOpen(false);
    if (forceReload) forceReload();
  };

  const onScaBlocked = (retry: () => void) => {
    setScaRetry({ run: retry });
    setScaBlockedOpen(true);
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>Change User Data</DialogTitle>
        <DialogContent>
          <ToggleButtonGroup
            value={mode}
            exclusive
            fullWidth
            size="small"
            sx={{ mb: 2, mt: 1 }}
            onChange={(event, value: ChangeDataMode | null) => {
              if (value) setMode(value);
            }}
          >
            <ToggleButton value="email">Email</ToggleButton>
            <ToggleButton value="phone">Phone</ToggleButton>
            <ToggleButton value="name">Name</ToggleButton>
            <ToggleButton value="address">Address</ToggleButton>
          </ToggleButtonGroup>
          {mode === 'email' ? <ChangeEmailForm userData={userData} onSuccess={onSuccess} onScaBlocked={onScaBlocked} /> : null}
          {mode === 'phone' ? <ChangePhoneForm userData={userData} onSuccess={onSuccess} onScaBlocked={onScaBlocked} /> : null}
          {mode === 'name' ? <ChangeNameForm userData={userData} onSuccess={onSuccess} onScaBlocked={onScaBlocked} /> : null}
          {mode === 'address' ? <ChangeAddressForm userData={userData} onSuccess={onSuccess} onScaBlocked={onScaBlocked} /> : null}
        </DialogContent>
      </Dialog>
      <ScaConsentRequiredDialog
        open={scaBlockedOpen}
        onClose={() => setScaBlockedOpen(false)}
        onSkip={
          scaRetry
            ? () => {
                setScaBlockedOpen(false);
                scaRetry.run();
              }
            : undefined
        }
      />
    </>
  );
}

const ChangeEmailForm: React.FC<{ userData: UserItem; onSuccess: (message: string) => void; onScaBlocked: (retry: () => void) => void }> = ({
  userData,
  onSuccess,
  onScaBlocked,
}) => {
  const [emailOld, setEmailOld] = React.useState(userData.email || '');
  const [emailNew, setEmailNew] = React.useState('');
  const [isLoading, setIsLoading] = React.useState(false);

  const submit = async (skipMangopayUpdate = false) => {
    setIsLoading(true);
    try {
      await putUserEmail({ emailOld, emailNew, skipMangopayUpdate });
      onSuccess(skipMangopayUpdate ? 'Email changed (Mangopay update skipped)' : 'Email changed successfully');
    } catch (error: any) {
      handleApiError(error, () => onScaBlocked(() => submit(true)));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box>
      <TextField fullWidth sx={{ mt: 1 }} label="Old email" type="email" value={emailOld} onChange={(e) => setEmailOld(e.target.value)} />
      <TextField fullWidth sx={{ mt: 2 }} label="New email" type="email" value={emailNew} onChange={(e) => setEmailNew(e.target.value)} />
      <LoadingButton
        loading={isLoading}
        disabled={!emailOld || !emailNew}
        fullWidth
        sx={{ mt: 2 }}
        color="primary"
        variant="contained"
        onClick={() => submit()}
      >
        Change Email
      </LoadingButton>
    </Box>
  );
};

const ChangePhoneForm: React.FC<{ userData: UserItem; onSuccess: (message: string) => void; onScaBlocked: (retry: () => void) => void }> = ({
  userData,
  onSuccess,
  onScaBlocked,
}) => {
  const [phone, setPhone] = React.useState(userData.phone || '');
  const [isLoading, setIsLoading] = React.useState(false);

  const submit = async (skipMangopayUpdate = false) => {
    setIsLoading(true);
    try {
      await putUserPhone({ userId: userData.userId, phone, skipMangopayUpdate });
      onSuccess(skipMangopayUpdate ? 'Phone changed (Mangopay update skipped)' : 'Phone changed successfully');
    } catch (error: any) {
      handleApiError(error, () => onScaBlocked(() => submit(true)));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box>
      <TextField fullWidth sx={{ mt: 1 }} label="Phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} />
      <LoadingButton loading={isLoading} disabled={!phone} fullWidth sx={{ mt: 2 }} color="primary" variant="contained" onClick={() => submit()}>
        Change Phone
      </LoadingButton>
    </Box>
  );
};

/**
 * Uploads a required document (FormData field `file`) to
 * /api/v1/customer/:customerId/residencyPermitAndUploadToXpecto and unlocks the
 * dependent form once the upload succeeded.
 */
const DocumentUploadGate: React.FC<{
  userId: string;
  documentLabel: string;
  uploaded: boolean;
  setUploaded: (uploaded: boolean) => void;
}> = ({ userId, documentLabel, uploaded, setUploaded }) => {
  const [file, setFile] = React.useState<File | null>(null);
  const [isUploading, setIsUploading] = React.useState(false);

  const upload = async () => {
    if (!file) return;
    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await postCustomerResidencyDoc(userId, formData);
      setUploaded(true);
      generalStore.setMessage(`${documentLabel} uploaded successfully`);
    } catch (error: any) {
      handleApiError(error);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Box sx={{ mt: 1 }}>
      <Typography variant="body2" sx={{ mb: 1 }}>
        Upload the {documentLabel} first. The fields below are enabled after a successful upload.
      </Typography>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        <TextField
          fullWidth
          size="small"
          type="file"
          onChange={(event) => {
            const input = event.target as HTMLInputElement;
            setFile(input.files?.[0] ?? null);
          }}
        />
        <LoadingButton loading={isUploading} disabled={!file || uploaded} variant="contained" color="primary" onClick={upload}>
          Upload
        </LoadingButton>
      </Box>
      {uploaded ? (
        <Alert severity="success" sx={{ mt: 1 }}>
          {documentLabel} uploaded — you can now change the data.
        </Alert>
      ) : null}
    </Box>
  );
};

const ChangeNameForm: React.FC<{ userData: UserItem; onSuccess: (message: string) => void; onScaBlocked: (retry: () => void) => void }> = ({
  userData,
  onSuccess,
  onScaBlocked,
}) => {
  const [certificateUploaded, setCertificateUploaded] = React.useState(false);
  const [firstName, setFirstName] = React.useState(userData.firstName || '');
  const [lastName, setLastName] = React.useState(userData.lastName || '');
  const [isLoading, setIsLoading] = React.useState(false);

  const submit = async (skipMangopayUpdate = false) => {
    setIsLoading(true);
    try {
      await putUserName({ userId: userData.userId, firstName, lastName, skipMangopayUpdate });
      onSuccess(skipMangopayUpdate ? 'Name changed (Mangopay update skipped)' : 'Name changed successfully');
    } catch (error: any) {
      handleApiError(error, () => onScaBlocked(() => submit(true)));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box>
      <DocumentUploadGate
        userId={userData.userId}
        documentLabel="name certificate"
        uploaded={certificateUploaded}
        setUploaded={setCertificateUploaded}
      />
      <TextField
        fullWidth
        sx={{ mt: 2 }}
        label="First name"
        disabled={!certificateUploaded}
        value={firstName}
        onChange={(e) => setFirstName(e.target.value)}
      />
      <TextField
        fullWidth
        sx={{ mt: 2 }}
        label="Last name"
        disabled={!certificateUploaded}
        value={lastName}
        onChange={(e) => setLastName(e.target.value)}
      />
      <LoadingButton
        loading={isLoading}
        disabled={!certificateUploaded || !firstName || !lastName}
        fullWidth
        sx={{ mt: 2 }}
        color="primary"
        variant="contained"
        onClick={() => submit()}
      >
        Change Name
      </LoadingButton>
    </Box>
  );
};

const ChangeAddressForm: React.FC<{ userData: UserItem; onSuccess: (message: string) => void; onScaBlocked: (retry: () => void) => void }> = ({
  userData,
  onSuccess,
  onScaBlocked,
}) => {
  const [meldezettelUploaded, setMeldezettelUploaded] = React.useState(false);
  const [street, setStreet] = React.useState(userData.personalInformation?.street || '');
  const [city, setCity] = React.useState(userData.personalInformation?.city || '');
  const [plz, setPlz] = React.useState(userData.personalInformation?.PLZ || '');
  const [country, setCountry] = React.useState<string>(
    countries.find((c) => c.alpha3 === userData.personalInformation?.domicile?.toLowerCase())?.alpha3 || '',
  );
  const [isLoading, setIsLoading] = React.useState(false);

  const submit = async (skipMangopayUpdate = false) => {
    setIsLoading(true);
    try {
      await putUserAddress({ userId: userData.userId, street, city, plz, country, skipMangopayUpdate });
      onSuccess(skipMangopayUpdate ? 'Address changed (Mangopay update skipped)' : 'Address changed successfully');
    } catch (error: any) {
      handleApiError(error, () => onScaBlocked(() => submit(true)));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box>
      <DocumentUploadGate
        userId={userData.userId}
        documentLabel="Meldezettel"
        uploaded={meldezettelUploaded}
        setUploaded={setMeldezettelUploaded}
      />
      <TextField
        fullWidth
        sx={{ mt: 2 }}
        label="Street"
        disabled={!meldezettelUploaded}
        value={street}
        onChange={(e) => setStreet(e.target.value)}
      />
      <TextField
        fullWidth
        sx={{ mt: 2 }}
        label="City"
        disabled={!meldezettelUploaded}
        value={city}
        onChange={(e) => setCity(e.target.value)}
      />
      <TextField
        fullWidth
        sx={{ mt: 2 }}
        label="Postal code (PLZ)"
        disabled={!meldezettelUploaded}
        value={plz}
        onChange={(e) => setPlz(e.target.value)}
      />
      <TextField
        fullWidth
        select
        sx={{ mt: 2 }}
        label="Country"
        disabled={!meldezettelUploaded}
        value={country}
        onChange={(e) => setCountry(e.target.value)}
      >
        {countries.map((c) => (
          <MenuItem key={c.alpha3} value={c.alpha3}>
            {c.name}
          </MenuItem>
        ))}
      </TextField>
      <LoadingButton
        loading={isLoading}
        disabled={!meldezettelUploaded || !street || !city || !plz || !country}
        fullWidth
        sx={{ mt: 2 }}
        color="primary"
        variant="contained"
        onClick={() => submit()}
      >
        Change Address
      </LoadingButton>
    </Box>
  );
};
